import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { useContracts } from '@/features/contracts/hooks/useContracts';
import { useDraftsList } from '@/features/contracts/hooks/useContractDrafts';
import { ContractBadge } from '@/features/contracts/components/ContractBadge';
import { DisputeBadge } from '@/features/contracts/components/DisputeBadge';
import { formatPrice, formatDate, formatFullName, formatPaymentType, getContractDisplayPrice } from '@/features/contracts/utils/contractFormatter';
import { SignatureStatusBadge } from '@/features/contracts/utils/signatureStatus';
import type { ContractDraft, ContractListItem, ContractFilters } from '@/features/contracts/types/contract.types';
import { DataList } from '@/Components/shared/List';
import { useListState, useListSearch } from '@/hooks/list';
import { paginationToSkipTake } from '@/utils/list.utils';
import type { ColumnDef, BaseListFilters } from '@/types/list.types';
import { FormInput } from '@/Components/shared/FormInput';
import { useAuthStore } from '@/Stores/auth-v2-store';
import { ContractsMobileCards } from './ContractsMobileCards';
import { ContractDraftsTab } from './ContractDraftsTab';

interface ContractsListFilters extends BaseListFilters {
  duration?: number;
  paymentType?: string;
  creationDateFrom?: string;
  creationDateTo?: string;
  startDateFrom?: string;
  startDateTo?: string;
  status?: string;
  /**
   * Deux filtres services indépendants, à trois états chacun : `undefined` = tous
   * (filtre inactif), `true` = uniquement avec, `false` = explicitement sans.
   * Des cases à cocher ne conviendraient pas : décochée, une case ne sait pas
   * exprimer « sans ce service », elle dit seulement « peu importe ».
   */
  hasUpsell?: boolean;
  hasAssistance?: boolean;
}

const BASE_DURATION_OPTIONS = [3, 6, 12, 24] as const;
const EXTENDED_DURATION_OPTIONS = [36, 48, 60] as const;

export interface ContractsListPageProps {
  /** Callback when a row is clicked. If undefined, rows are not clickable. */
  onRowClick?: (contract: ContractListItem) => void;
  /** Custom header actions (buttons). If undefined, no header actions. */
  headerActions?: React.ReactNode;
  /** Page title. Default: "Contrats" */
  title?: string;
  /** Default filters to merge (e.g. companyId for vendor-scoped lists) */
  defaultFilters?: Partial<ContractFilters>;
  /** Callback to notify parent of total count changes */
  onTotalChange?: (total: number) => void;
  /** Embedded mode (no outer padding/margins). Default: false */
  embedded?: boolean;
  /** Hide the DataList title (when the parent renders its own header). Default: false */
  hideTitle?: boolean;
  /** Afficher les badges de status counts (actifs, expirés, annulés) dans le header */
  showStatusCounts?: boolean;
  /** Afficher l'onglet "À finaliser" (brouillons). Default: false */
  showDraftsTab?: boolean;
  /** Callback appelé lorsqu'un brouillon est repris. Requis si showDraftsTab=true */
  onDraftResume?: (draft: ContractDraft) => void;
}

type TabId = 'contracts' | 'drafts';

/**
 * Composant partagé pour la liste des contrats
 * Utilisé par admin et outil-pro avec des props différents
 */
export const ContractsListPage: React.FC<ContractsListPageProps> = ({
  onRowClick,
  headerActions,
  title = 'Contrats',
  defaultFilters,
  onTotalChange,
  embedded = false,
  hideTitle = false,
  showStatusCounts = false,
  showDraftsTab = false,
  onDraftResume,
}) => {
  const { currentUser, society } = useAuthStore();

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab: TabId = (searchParams.get('tab') as TabId) === 'drafts' && showDraftsTab
    ? 'drafts'
    : 'contracts';
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);

  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === 'drafts') {
      next.set('tab', 'drafts');
    } else {
      next.delete('tab');
    }
    setSearchParams(next);
  };

  const { data: drafts } = useDraftsList();
  const draftsCount = showDraftsTab ? drafts?.length ?? 0 : 0;

  const {
    filters,
    updateFilter,
    pagination,
    setPagination,
    sortConfig,
    setSortConfig,
    resetFilters,
    activeFiltersCount,
  } = useListState<ContractsListFilters>({
    defaultLimit: 20,
    defaultSortBy: 'dateCreation',
    defaultSortDirection: 'DESC',
    persistInUrl: true,
    filterTypes: { duration: 'number', hasUpsell: 'boolean', hasAssistance: 'boolean' },
  });

  const { searchTerm, debouncedSearch, setSearchTerm } = useListSearch({
    debounceMs: 300,
    persistInUrl: true,
  });

  const apiFilters = useMemo(() => {
    const { skip, take } = paginationToSkipTake(pagination.page, pagination.limit);
    return {
      skip,
      take,
      search: debouncedSearch,
      duration: filters.duration,
      paymentType: filters.paymentType,
      creationDateFrom: filters.creationDateFrom,
      creationDateTo: filters.creationDateTo,
      startDateFrom: filters.startDateFrom,
      startDateTo: filters.startDateTo,
      status: filters.status,
      hasUpsell: filters.hasUpsell,
      hasAssistance: filters.hasAssistance,
      sortBy: sortConfig.sortBy,
      sortDirection: sortConfig.sortDirection,
      ...defaultFilters,
    };
  }, [pagination, debouncedSearch, filters.duration, filters.paymentType, filters.creationDateFrom, filters.creationDateTo, filters.startDateFrom, filters.startDateTo, filters.status, filters.hasUpsell, filters.hasAssistance, sortConfig, defaultFilters]);

  const { data, isLoading, error } = useContracts(apiFilters);

  const contracts = useMemo(() => data?.contracts || [], [data]);
  const total = useMemo(() => data?.total || 0, [data]);

  useEffect(() => {
    onTotalChange?.(total);
  }, [total, onTotalChange]);

  // Queries légères pour les counts par statut (conditionnel)
  const { data: activeData } = useContracts(showStatusCounts ? { ...defaultFilters, take: 1, status: 1 } : undefined);
  const { data: expiredData } = useContracts(showStatusCounts ? { ...defaultFilters, take: 1, status: 2 } : undefined);
  const { data: cancelledData } = useContracts(showStatusCounts ? { ...defaultFilters, take: 1, status: 3 } : undefined);

  const statusBadges = showStatusCounts ? (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 sm:tw-gap-3">
      {activeData?.total != null && (
        <span className="tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-rounded-full tw-bg-green-100 tw-text-green-700">
          {activeData.total} actif{activeData.total > 1 ? 's' : ''}
        </span>
      )}
      {expiredData?.total != null && (
        <span className="tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-rounded-full tw-bg-blue-100 tw-text-blue-700">
          {expiredData.total} expiré{expiredData.total > 1 ? 's' : ''}
        </span>
      )}
      {cancelledData?.total != null && (
        <span className="tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-rounded-full tw-bg-red-100 tw-text-red-700">
          {cancelledData.total} annulé{cancelledData.total > 1 ? 's' : ''}
        </span>
      )}
    </div>
  ) : undefined;

  const isInternal = currentUser?.type === 'INTERNAL';

  const hasMonthlyEnabled = useMemo(() => {
    return isInternal || contracts.some((c) => c.company?.monthlyPayment === 2);
  }, [isInternal, contracts]);

  const hasOnlineEnabled = useMemo(() => {
    return isInternal || contracts.some((c) => c.company?.onlinePayment === 2);
  }, [isInternal, contracts]);

  const showPaymentTypeUI = hasMonthlyEnabled || hasOnlineEnabled;

  const availableDurations = useMemo(() => {
    if (currentUser?.type === 'INTERNAL') {
      return [...BASE_DURATION_OPTIONS, ...EXTENDED_DURATION_OPTIONS];
    }
    if (currentUser?.type === 'EXTERNAL' && (society as any)?.extendedDuration) {
      return [...BASE_DURATION_OPTIONS, ...EXTENDED_DURATION_OPTIONS];
    }
    return [...BASE_DURATION_OPTIONS];
  }, [currentUser?.type, society]);

  const columns: ColumnDef<ContractListItem>[] = useMemo(() => {
    const baseColumns: ColumnDef<ContractListItem>[] = [
      {
        key: 'company',
        label: 'Société',
        render: (contract) => (
          <div>
            <div className="tw-text-sm tw-font-medium">{contract.company?.name || '-'}</div>
            {contract.creator && (
              <div className="tw-text-xs tw-text-gray-500">
                {formatFullName(contract.creator.firstName, contract.creator.lastName)}
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'label',
        label: 'Contrat',
        render: (contract) => (
          <div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-text-sm tw-font-medium">{contract.label}</span>
              {!!contract.isUpsell && (
                <span className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-bold tw-bg-indigo-100 tw-text-indigo-700">UpSell</span>
              )}
              {contract.assistanceFormulaId != null && (
                <span
                  title={contract.assistanceLabel ?? undefined}
                  className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-bold tw-bg-teal-100 tw-text-teal-700"
                >
                  Assist
                </span>
              )}
            </div>
            {contract.externalId && contract.externalId !== '0' && (
              <div className="tw-text-xs tw-text-gray-500">{contract.externalId}</div>
            )}
            {contract.warranty?.name && (
              <div className="tw-text-xs tw-text-gray-500">{contract.warranty.name}</div>
            )}
          </div>
        ),
      },
      {
        key: 'registration',
        label: 'Immat.',
        render: (contract) => {
          const vehicle = contract.vehicle;
          const registration = vehicle?.registration;
          const brand = vehicle?.brand;
          const model = vehicle?.model;
          const vehicleInfo = [brand, model].filter(Boolean).join(' ');

          return (
            <div>
              <div className="tw-text-sm tw-font-medium">{registration || '-'}</div>
              {vehicleInfo && (
                <div className="tw-text-xs tw-text-gray-500">{vehicleInfo}</div>
              )}
            </div>
          );
        },
      },
      {
        key: 'beneficiary',
        label: 'Bénéficiaire',
        render: (contract) => (
          <div>
            <div className="tw-text-sm tw-font-medium">
              {contract.beneficiary
                ? formatFullName(contract.beneficiary.firstName, contract.beneficiary.lastName)
                : '-'}
            </div>
            {contract.beneficiary?.email && (
              <div className="tw-text-xs tw-text-gray-500">{contract.beneficiary.email}</div>
            )}
          </div>
        ),
      },
    ];

    if (showPaymentTypeUI) {
      baseColumns.push({
        key: 'monthlyPaymentId',
        label: 'Type de paiement',
        render: (contract) => formatPaymentType(contract.monthlyPaymentId),
      });
    }

    baseColumns.push(
      {
        key: 'sellingPrice',
        label: 'Prix de vente',
        render: (contract) => {
          const price = getContractDisplayPrice(contract);
          return (
            <span className="tw-font-medium tw-text-green-600">
              {price.kind === 'cash'
                ? formatPrice(price.total)
                : `${price.duration} × ${formatPrice(price.monthly)}`}
            </span>
          );
        },
      },
      {
        key: 'duration',
        label: 'Durée',
        render: (contract) => (contract.duration ? `${contract.duration} mois` : '-'),
      },
      {
        key: 'dateCreation',
        label: 'Date création',
        render: (contract) => {
          if (!contract.dateCreation) return '-';
          const date = new Date(contract.dateCreation);
          const dateStr = new Intl.DateTimeFormat('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }).format(date);
          const timeStr = new Intl.DateTimeFormat('fr-FR', {
            hour: '2-digit',
            minute: '2-digit',
          }).format(date);

          return (
            <div>
              <div className="tw-text-sm tw-font-medium">{dateStr}</div>
              <div className="tw-text-xs tw-text-gray-500">{timeStr}</div>
            </div>
          );
        },
      },
      {
        key: 'dateSouscription',
        label: 'Date début',
        render: (contract) => formatDate(contract.dateSouscription),
      },
      {
        key: 'signature',
        label: 'Signature',
        render: (contract) => <SignatureStatusBadge status={contract.signatureStatus ?? null} />,
      },
      {
        key: 'idStatus',
        label: 'Statut',
        render: (contract) => (
          <div className="tw-inline-flex tw-flex-wrap tw-items-center tw-gap-1">
            <ContractBadge status={contract.idStatus} monthlyPaymentId={contract.monthlyPaymentId} monthlyPaymentStatus={contract.monthlyPaymentStatus} cancelationRequested={contract.cancelationRequested} cancelationRequestedAt={contract.cancelationRequestedAt} />
            {contract.latestDisputeStatus && <DisputeBadge status={contract.latestDisputeStatus} />}
          </div>
        ),
      },
    );

    return baseColumns;
  }, [showPaymentTypeUI]);

  const cancelationRowClassName = useMemo(() => {
    if (currentUser?.type !== 'INTERNAL') return undefined;
    return (contract: ContractListItem) => (contract.cancelationRequested && contract.idStatus !== 3) ? 'tw-bg-red-50' : '';
  }, [currentUser?.type]);

  const tabsHeader = showDraftsTab ? (
    <div className={embedded ? 'tw-pt-4 tw-mb-4' : 'tw-pt-8 tw-mb-4'}>
      <div className="tw-flex tw-items-center tw-justify-between">
        <div className="tw-flex tw-items-center tw-gap-3">
          <FileText className="tw-w-8 tw-h-8 tw-text-affinicar-primary" />
          <div className="tw-flex tw-items-center tw-gap-3">
            <button
              type="button"
              onClick={() => handleTabChange('contracts')}
              className={`tw-text-2xl tw-font-bold tw-transition-colors tw-pb-1 ${
                activeTab === 'contracts'
                  ? 'tw-text-gray-900 tw-border-b-2 tw-border-affinicar-primary'
                  : 'tw-text-gray-500 hover:tw-text-gray-700 tw-cursor-pointer'
              }`}
            >
              {title}
              {total > 0 && (
                <span className="tw-ml-1 tw-text-base tw-font-normal tw-text-gray-400">
                  ({total} {total > 1 ? 'éléments' : 'élément'})
                </span>
              )}
            </button>

            {draftsCount > 0 && (
              <>
                <span className="tw-text-2xl tw-text-gray-200 tw-font-light">|</span>
                <button
                  type="button"
                  onClick={() => handleTabChange('drafts')}
                  className={`tw-text-2xl tw-font-bold tw-transition-colors tw-pb-1 ${
                    activeTab === 'drafts'
                      ? 'tw-text-gray-900 tw-border-b-2 tw-border-affinicar-primary'
                      : 'tw-text-gray-500 hover:tw-text-gray-700 tw-cursor-pointer'
                  }`}
                >
                  À finaliser
                  <span className="tw-ml-1.5 tw-text-xs tw-font-semibold tw-bg-amber-100 tw-text-amber-700 tw-rounded-full tw-px-2 tw-py-0.5 tw-align-middle">
                    {draftsCount}
                  </span>
                </button>
              </>
            )}
          </div>
        </div>

        {headerActions && <div>{headerActions}</div>}
      </div>
    </div>
  ) : null;

  if (showDraftsTab && activeTab === 'drafts') {
    if (!onDraftResume) {
      return <div>onDraftResume est requis lorsque showDraftsTab est activé</div>;
    }
    return (
      <div>
        {tabsHeader}
        <ContractDraftsTab onResume={onDraftResume} />
      </div>
    );
  }

  const dataList = (
    <DataList
      embedded={embedded}
      data={contracts}
      total={total}
      isLoading={isLoading}
      error={error as Error}
      title={hideTitle || showDraftsTab ? undefined : title}
      titleIcon={hideTitle || showDraftsTab ? undefined : FileText}
      showCount={!hideTitle && !showDraftsTab}
      customHeaderLeft={statusBadges}
      rowClassName={cancelationRowClassName}
      headerActions={showDraftsTab ? undefined : headerActions}
      columns={columns}
      filterSearch={
        <div className="tw-flex tw-flex-col tw-gap-1">
          <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Recherche</span>
          <FormInput
            type="text"
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Rechercher..."
            size="sm"
          />
        </div>
      }
      filters={
        <>
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Durée</span>
            <select
              value={filters.duration?.toString() ?? ''}
              onChange={(e) => updateFilter('duration', e.target.value ? Number(e.target.value) : undefined)}
              className="tw-w-full tw-px-2.5 tw-pr-2.5 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary"
            >
              <option value="">Toutes</option>
              {availableDurations.map((duration) => (
                <option key={duration} value={duration}>
                  {duration} mois
                </option>
              ))}
            </select>
          </div>

          {showPaymentTypeUI && (
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Type de paiement</span>
              <select
                value={filters.paymentType ?? ''}
                onChange={(e) => updateFilter('paymentType', e.target.value || undefined)}
                className="tw-w-full tw-px-2.5 tw-pr-2.5 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary"
              >
                <option value="">Tous</option>
                {hasMonthlyEnabled && <option value="monthly">Mensuel</option>}
                <option value="cash">Comptant</option>
                {hasOnlineEnabled && <option value="online">Comptant en ligne</option>}
              </select>
            </div>
          )}

          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Date création</span>
            <div className="tw-flex tw-gap-2">
              <FormInput type="date" size="sm" value={filters.creationDateFrom ?? ''} onChange={(value) => updateFilter('creationDateFrom', value || undefined)} placeholder="Du" />
              <FormInput type="date" size="sm" value={filters.creationDateTo ?? ''} onChange={(value) => updateFilter('creationDateTo', value || undefined)} placeholder="Au" />
            </div>
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Date début</span>
            <div className="tw-flex tw-gap-2">
              <FormInput type="date" size="sm" value={filters.startDateFrom ?? ''} onChange={(value) => updateFilter('startDateFrom', value || undefined)} placeholder="Du" />
              <FormInput type="date" size="sm" value={filters.startDateTo ?? ''} onChange={(value) => updateFilter('startDateTo', value || undefined)} placeholder="Au" />
            </div>
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Affinicar Upsell</span>
            <select
              value={filters.hasUpsell !== undefined ? String(filters.hasUpsell) : ''}
              onChange={(e) => updateFilter('hasUpsell', e.target.value ? e.target.value === 'true' : undefined)}
              className="tw-w-full tw-px-2.5 tw-pr-2.5 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary"
            >
              <option value="">Tous</option>
              <option value="true">Oui</option>
              <option value="false">Non</option>
            </select>
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Affinicar Assist</span>
            <select
              value={filters.hasAssistance !== undefined ? String(filters.hasAssistance) : ''}
              onChange={(e) => updateFilter('hasAssistance', e.target.value ? e.target.value === 'true' : undefined)}
              className="tw-w-full tw-px-2.5 tw-pr-2.5 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary"
            >
              <option value="">Tous</option>
              <option value="true">Oui</option>
              <option value="false">Non</option>
            </select>
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Statut</span>
            <select
              value={filters.status?.toString() ?? ''}
              onChange={(e) => updateFilter('status', e.target.value || undefined)}
              className="tw-w-full tw-px-2.5 tw-pr-2.5 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary"
            >
              <option value="">Tous</option>
              <option value="pending_cancellation">En attente d'annulation</option>
              <option value="payment_initialized">Paiement initialisé</option>
              <option value="payment_ongoing">En cours de paiement</option>
              <option value="active">Actif</option>
              <option value="payment_failed">Paiement échoué</option>
              <option value="cancelled">Annulé</option>
              <option value="expired">Expiré</option>
              <option value="dispute">Litige</option>
            </select>
          </div>
        </>
      }
      activeFiltersCount={activeFiltersCount}
      onResetFilters={resetFilters}
      pagination={pagination}
      onPaginationChange={setPagination}
      sortConfig={sortConfig}
      onSortChange={setSortConfig}
      mobileCardView={ContractsMobileCards}
      onRowClick={onRowClick}
      getRowKey={(contract) => contract.id}
      emptyMessage="Aucun contrat trouvé"
    />
  );

  if (showDraftsTab) {
    return (
      <div>
        {tabsHeader}
        {dataList}
      </div>
    );
  }

  return dataList;
};
